import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useFormContext } from '../contexts/FormContext';
import {
  safeGetItem,
  safeSetItem,
} from '../utils/storageUtils';

function readBuilderViewMode(formId) {
  if (!formId || typeof window === 'undefined') {
    return 'auto';
  }

  try {
    const raw = sessionStorage.getItem(
      `form_${formId}_layoutVersionMode`
    );
    const parsed = raw ? JSON.parse(raw) : null;

    return parsed?.value || 'auto';
  } catch (error) {
    console.warn(
      '[useFormIntegrationConfigs] Failed to read builder mode:',
      error
    );

    return 'auto';
  }
}

function getEffectiveViewMode({
  formId,
  pathname,
}) {
  if (!formId) {
    return 'auto';
  }

  const isBuilderRoute = pathname.startsWith('/form-builder');

  if (!isBuilderRoute) {
    return 'published';
  }

  return readBuilderViewMode(formId);
}

export function getFormIntegrationConfigsStorageKey({
  formId,
  viewMode,
}) {
  if (!formId) {
    return null;
  }

  return `form_${formId}_integrationConfigs__${viewMode || 'auto'}`;
}

export default function useFormIntegrationConfigs() {
  const location = useLocation();
  const { formId } = useFormContext();

  const effectiveViewMode = useMemo(() => {
    return getEffectiveViewMode({
      formId,
      pathname: location.pathname || '',
    });
  }, [
    formId,
    location.pathname,
  ]);

  const storageKey = useMemo(() => {
    return getFormIntegrationConfigsStorageKey({
      formId,
      viewMode: effectiveViewMode,
    });
  }, [
    formId,
    effectiveViewMode,
  ]);

  const [integrationConfigs, setIntegrationConfigs] = useState({});

  useEffect(() => {
    if (!storageKey) {
      setIntegrationConfigs({});
      return;
    }

    const storedConfigs = safeGetItem(storageKey) || {};

    if (
      storedConfigs &&
      typeof storedConfigs === 'object' &&
      !Array.isArray(storedConfigs)
    ) {
      setIntegrationConfigs(storedConfigs);
      return;
    }

    setIntegrationConfigs({});
  }, [storageKey]);

  const persistConfigs = useCallback((nextConfigs) => {
    const safeNext =
      nextConfigs &&
      typeof nextConfigs === 'object' &&
      !Array.isArray(nextConfigs)
        ? nextConfigs
        : {};

    setIntegrationConfigs(safeNext);

    if (storageKey) {
      safeSetItem(storageKey, safeNext);
    }

    return safeNext;
  }, [storageKey]);

  const getProviderConfig = useCallback((provider) => {
    if (!provider) {
      return null;
    }

    return integrationConfigs?.[provider] || null;
  }, [integrationConfigs]);

  const setProviderConfig = useCallback((provider, config = {}) => {
    if (!provider) {
      return null;
    }

    const nextConfig = {
      provider,
      ...config,
      updatedAt: config.updatedAt || new Date().toISOString(),
      localStatus: config.localStatus || 'staged',
    };

    const nextConfigs = {
      ...(integrationConfigs || {}),
      [provider]: nextConfig,
    };

    persistConfigs(nextConfigs);

    return nextConfig;
  }, [
    integrationConfigs,
    persistConfigs,
  ]);

  const updateProviderConfig = useCallback((provider, patchOrUpdater) => {
    if (!provider) {
      return null;
    }

    const currentConfig = integrationConfigs?.[provider] || {
      provider,
    };

    const patch =
      typeof patchOrUpdater === 'function'
        ? patchOrUpdater(currentConfig)
        : patchOrUpdater || {};

    const nextConfig = {
      ...currentConfig,
      ...patch,
      provider,
      updatedAt: new Date().toISOString(),
      localStatus: 'staged',
    };

    const nextConfigs = {
      ...(integrationConfigs || {}),
      [provider]: nextConfig,
    };

    persistConfigs(nextConfigs);

    return nextConfig;
  }, [
    integrationConfigs,
    persistConfigs,
  ]);

  return {
    formId,
    effectiveViewMode,
    storageKey,
    integrationConfigs,
    getProviderConfig,
    setProviderConfig,
    updateProviderConfig,
  };
}
